import { AttrValue } from '@hyphengroup/cms-components/types/attrNames';
import { currencyFormatterFactory } from '@hyphengroup/cms-components/utils/number';
import { PersonalLoanRateInterval } from '__generated__/graphql';
import { BaseAttribute } from '$/(cms)/_modules/cmsService/types';

const formatAttribute = (
  attr?: string,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
) => {
  if (!attr) {
    return attr;
  }
  return currencyFormatterFactory({
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(+attr);
};

export type CalculatedLoanValues =
  | {
      readonly monthlyRepayment: string | undefined;
      readonly totalFees: string | undefined;
      readonly totalInterest: string | undefined;
      readonly totalRepayment: string | undefined;
      readonly annualisedPercentageRate: string | undefined;
      readonly annualRate: string | undefined;
      readonly dailyInterest: string | undefined;
      readonly introductoryAnnualRate: string | undefined;
      readonly introductoryRateOnPrincipalReducing: string | undefined;
      readonly fee:
        | { readonly fee: string | undefined }
        | {
            readonly fee: string | undefined;
            readonly feeInterval: PersonalLoanRateInterval | undefined;
          }
        | undefined;
      readonly interest:
        | {
            readonly interestInterval: PersonalLoanRateInterval | undefined;
            readonly interestRate: string | undefined;
          }
        | undefined;
    }
  | undefined;

/**
 * What this helper writes, and how. The names are the keys, so the list and the
 * writers cannot drift: adding a measure here is what makes it recalculated and
 * what makes `RECALCULATED_ATTRIBUTE_NAMES` know about it.
 */
const RECALCULATED_ATTRIBUTES = {
  MONTHLY_REPAYMENT: (v) => formatAttribute(v?.monthlyRepayment),
  HANDLING_FEE: (v) => formatAttribute(v?.totalFees),
  TOTAL_INTEREST: (v) => formatAttribute(v?.totalInterest),
  TOTAL_REPAYMENT: (v) => formatAttribute(v?.totalRepayment),
  APR: (v) => v?.annualisedPercentageRate?.replace('%', ''),
  ANNUAL_RATE: (v) => v?.interest?.interestRate?.replace('%', ''),
  INTRODUCTORY_ANNUAL_RATE: (v) =>
    formatAttribute(v?.introductoryAnnualRate, 0, 2),
  DAILY_INTEREST: (v) => v?.dailyInterest,
  INTRODUCTORY_MONTHLY_REPAYMENT: (v) =>
    formatAttribute(v?.introductoryRateOnPrincipalReducing),
} satisfies Partial<
  Record<
    AttrValue,
    (values: NonNullable<CalculatedLoanValues>) => string | undefined
  >
>;

/**
 * These are authored empty for personal loans — the number comes from the
 * product's rate table for a given amount and tenure — so an empty one means
 * "not priceable at these inputs", not "not configured".
 */
export const RECALCULATED_ATTRIBUTE_NAMES = Object.keys(
  RECALCULATED_ATTRIBUTES,
) as readonly AttrValue[];

export const RecalculateProductAttributes = (
  attributes: BaseAttribute[] | undefined,
  newAttributes: CalculatedLoanValues,
) => {
  if (!attributes) {
    return [];
  }

  return attributes.map((attr_) => {
    const attr = { ...(attr_ ?? {}) };
    if (!attr || !attr?.name?.name) {
      return attr;
    }

    if (attr.__typename === 'ComponentAttributesAttrNumberRange') {
      return attr;
    }

    const attributeName = attr.name.name as AttrValue;
    const write =
      RECALCULATED_ATTRIBUTES[
        attributeName as keyof typeof RECALCULATED_ATTRIBUTES
      ];
    if (write) attr.value = newAttributes ? write(newAttributes) : '';

    return attr;
  });
};
